import React, { useContext, useEffect, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import '../../../css/Flyout.scss';
import '../../../css/FlyoutTeacherNotesPanel.scss';
import '../../../css/FlyoutRubricPanel.scss';
import '../../../css/Accessibility.scss';

import lessonService from '../../services/LessonService';
import leftRailService from '../../services/LeftRailService';
import rightRailService, { RightRailTab } from '../../services/RightRailService';
import navMenuService from '../../services/NavMenuService';
import studentActivityService from '../../services/StudentActivityService';
import videoService from '../../services/VideoService';
import utilsService from '../../services/UtilsService';

import { ContentType, FlyoutOpenState, FlyoutSide, FlyoutTabs, LessonMode, LessonSlideIds, ScoreState } from '../../../Constants';

import { register } from '../../../i18n';

import useAccessibilityClick from '../../../hooks/useAccessibilityClick';

import AlignmentPanel from './AlignmentPanel';
import EngagementPanel from './EngagementPanel';
import FlyupNavForScoring from './FlyupNavForScoring';
import RubricPanel from './RubricPanel';
import StudentScorePanel from './StudentScorePanel';
import TeacherAIScoreResultsPanel from './TeacherAIScoreResultsPanel';
import TeacherNotesPanel from './TeacherNotesPanel';
import TeacherScorePanel from './TeacherScorePanel';
import CustomTeacherTabPanel from './CustomTeacherTabPanel';
import { getCustomTeacherTabId, getVisibleCustomTeacherTabs, isCustomTeacherTabId } from '../../services/CustomTeacherTabService';

const t = register('PanelMenu');
const t2 = register('AriaLabels');
const t3 = register('TeacherScoringPanel');

// Track if we've initialized the scoring flyout side from CSS variable
let hasInitializedScoringFlyoutSideFromTheme = false;

const Flyout = observer(({
  embedSide,
  flyoutSide,
  isActivity,
  isScoreModeVideoActivityParent,
  isScoring,
  lessonElementId,
  variant = 'full',
} = {}) => {
  const {
    lessonManager,
    navMenuManager,
    alignmentManager,
    userManager,
    scoringManager,
    responseManager,
    studentActivityManager
  } = useContext(MobXProviderContext);

  const model = lessonManager.getSlideModel(lessonElementId);
  const mode = lessonManager.playerMode;
  const [internalFlyoutSide, setInternalFlyoutSide] = useState((flyoutSide) || FlyoutSide.RIGHT);
  const lessonElementState = responseManager.lessonElementStates.get(lessonElementId);
  const isSummary = (lessonManager.currentLessonElementId === LessonSlideIds.SUMMARY_PAGE);

  const flipButton = useRef();
  const alignButton = useRef();
  const rubricButton = useRef();
  const aiScoreButton = useRef();
  const teacherNotes = useRef();
  const scoreButton = useRef();
  const engageButton = useRef();

  const isTeacher = userManager.canViewAsTeacher;
  const { isStudent } = userManager;
  const { hasViewEngagementDataPermission } = userManager;
  const hasAlignments = alignmentManager.hasAlignments(lessonElementId);
  // eslint-disable-next-line eqeqeq
  const hasRubric = (scoringManager.rubricMap.has(lessonElementId) && model.type !== ContentType.RUBRIC.type);

  const showAlignTab = lessonService.showFlyoutAlignments(hasAlignments);
  const showRubricTab = lessonService.showFlyoutRubric(hasRubric, lessonElementId);
  const showScoreTab = lessonService.showFlyoutScore();
  const showAIScoreTab = lessonElementState && (
    lessonElementState.scoreState === ScoreState.AI_SCORED || lessonElementState.scoreState === ScoreState.AI_OVERRIDDEN
  );
  const showNotesTab = lessonService.showFlyoutTeacherNotes() && !utilsService.isNullOrEmpty(model.teacherNotes);
  const customTeacherTabs = lessonService.showFlyoutTeacherNotes() ? getVisibleCustomTeacherTabs(model) : [];
  const showEngagementTab = hasViewEngagementDataPermission && isTeacher && (mode === LessonMode.SCORING) && lessonElementState;
  const showFlipButton = variant === 'full' && lessonService.showFlyoutFlip();
  const embedded = variant === 'tabs' || variant === 'panel';
  const embeddedInRightRail = embedded && embedSide === 'right';

  const selectATab = (closed, currentTab) => {
    if (closed) {
      return FlyoutTabs.NONE;
    } else {
      if (!currentTab || (currentTab && currentTab === FlyoutTabs.NONE)) {
        // this if stack is priority order
        // if this proves to not work in every case
        // the lesson mode might need to be queried and this should
        // go into a service
        if (showScoreTab) {
          return FlyoutTabs.SCORE;
        } else if (showAlignTab) {
          return FlyoutTabs.ALIGN;
        } else if (showNotesTab) {
          return FlyoutTabs.NOTES;
        } else if (customTeacherTabs.length > 0) {
          return getCustomTeacherTabId(customTeacherTabs[0]);
        } else if (showRubricTab) {
          navMenuService.toggleFlyoutPanel(false, FlyoutOpenState.OPEN_WIDE, lessonElementId);
          return FlyoutTabs.RUBRIC;
        } else if (showEngagementTab) {
          return FlyoutTabs.ENGAGEMENT;
        } else if (showAIScoreTab) {
          return FlyoutTabs.AI_SCORE;
        }
      }
    }
    return currentTab;
  };

  const openState = navMenuManager.getFlyoutOpenState(lessonElementId);
  const isFlyoutTabSelected = (tab) => {
    if (!openState) {
      return false;
    }
    if (embeddedInRightRail) {
      return rightRailService.selectedTab === RightRailTab.FLYOUT && openState.tab === tab;
    }
    if (embedded) {
      // Match other left-rail tabs: bold only when the rail is open and this
      // flyout panel is the active selection.
      return leftRailService.isOpen &&
        navMenuManager.leftRailSelectedTab === 'flyout' &&
        openState.tab === tab;
    }
    return openState.tab === tab && openState.open !== FlyoutOpenState.CLOSED;
  };

  const activateContentTab = (innerTab, nextOpenState) => {
    if (embeddedInRightRail) {
      rightRailService.toggleFlyoutTab(innerTab, nextOpenState, lessonElementId);
      return;
    }
    leftRailService.toggleFlyoutContentTab(innerTab, nextOpenState, lessonElementId);
  };

  useEffect(() => {
    // Only initialize from CSS variable once, not on every mount
    // This allows the default to be set from the theme, but preserves user changes
    if (!hasInitializedScoringFlyoutSideFromTheme) {
      const themeScoringFlyoutSide = getComputedStyle(document.documentElement).getPropertyValue('--scoring-flyout-side');
      if (themeScoringFlyoutSide) {
        navMenuManager.setScoringFlyoutSide(themeScoringFlyoutSide === 'left' ? FlyoutSide.LEFT : FlyoutSide.RIGHT);
      }
      hasInitializedScoringFlyoutSideFromTheme = true;
    }
  }, []);

  useEffect(() => {
    if (openState) {
      if (openState.open !== FlyoutOpenState.CLOSED) {
        if ((openState.tab === FlyoutTabs.SCORE && !showScoreTab) ||
            (openState.tab === FlyoutTabs.ALIGN && !showAlignTab) ||
            (openState.tab === FlyoutTabs.NOTES && !showNotesTab) ||
            (isCustomTeacherTabId(openState.tab) && !customTeacherTabs.some((tab) => getCustomTeacherTabId(tab) === openState.tab)) ||
            (openState.tab === FlyoutTabs.RUBRIC && !showRubricTab) ||
            (openState.tab === FlyoutTabs.ENGAGEMENT && !showEngagementTab) ||
            (openState.tab === FlyoutTabs.NONE)) {
          const tab = selectATab(false, FlyoutTabs.NONE);
          navMenuService.setFlyoutPanelTab(tab, lessonElementId);
        }
      }
      if (embedded) {
        setInternalFlyoutSide(embeddedInRightRail ? FlyoutSide.RIGHT : FlyoutSide.LEFT);
      } else if (isScoring && leftRailService.hasLeftRail()) {
        setInternalFlyoutSide(FlyoutSide.RIGHT);
      } else if (isScoring) {
        setInternalFlyoutSide(navMenuManager.scoringFlyoutSide);
      } else if (flyoutSide) {
        setInternalFlyoutSide(flyoutSide);
      } else {
        setInternalFlyoutSide(openState.side);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openState]);

  useAccessibilityClick(flipButton, () => {
    const os = navMenuManager.getFlyoutOpenState(lessonElementId);
    if (internalFlyoutSide === FlyoutSide.LEFT) {
      isScoring && navMenuManager.setScoringFlyoutSide(FlyoutSide.RIGHT);
      setInternalFlyoutSide(FlyoutSide.RIGHT);
      navMenuManager.setFlyoutOpenState({ open: os.open, tab: os.tab, side: FlyoutSide.RIGHT }, lessonElementId);
    } else {
      if (os.open !== FlyoutOpenState.CLOSED) {
        leftRailService.prepareInLessonLeftPanelOpen();
      }
      isScoring && navMenuManager.setScoringFlyoutSide(FlyoutSide.LEFT);
      setInternalFlyoutSide(FlyoutSide.LEFT);
      navMenuManager.setFlyoutOpenState({ open: os.open, tab: os.tab, side: FlyoutSide.LEFT }, lessonElementId);
    }
  });

  useAccessibilityClick(rubricButton, () => {
    setTimeout(() => {
      const announcementElement = document.getElementById(`${lessonElementId}-tab-announcement`);
      if (announcementElement) {
        announcementElement.textContent = `${t('rubricPanel')} tab opened`;
      }
    }, 100);

    activateContentTab(FlyoutTabs.RUBRIC, FlyoutOpenState.OPEN_WIDE, lessonElementId);
  });

  useAccessibilityClick(scoreButton, () => {
    setTimeout(() => {
      const announcementElement = document.getElementById(`${lessonElementId}-tab-announcement`);
      if (announcementElement) {
        announcementElement.textContent = `${t3('headerLabelScore')} tab opened`;
      }
    }, 100);

    activateContentTab(FlyoutTabs.SCORE, FlyoutOpenState.OPEN, lessonElementId);
  });

  useAccessibilityClick(teacherNotes, () => {
    setTimeout(() => {
      const announcementElement = document.getElementById(`${lessonElementId}-tab-announcement`);
      if (announcementElement) {
        announcementElement.textContent = `${t('teacherNotePanel')} tab opened`;
      }
    }, 100);

    activateContentTab(FlyoutTabs.NOTES, FlyoutOpenState.OPEN, lessonElementId);
  });

  useAccessibilityClick(alignButton, () => {
    setTimeout(() => {
      const announcementElement = document.getElementById(`${lessonElementId}-tab-announcement`);
      if (announcementElement) {
        announcementElement.textContent = `${t('alignmentPanel')} tab opened`;
      }
    }, 100);

    activateContentTab(FlyoutTabs.ALIGN, FlyoutOpenState.OPEN, lessonElementId);
  });

  useAccessibilityClick(engageButton, () => {
    setTimeout(() => {
      const announcementElement = document.getElementById(`${lessonElementId}-tab-announcement`);
      if (announcementElement) {
        announcementElement.textContent = `${t('engagementPanel')} tab opened`;
      }
    }, 100);

    activateContentTab(FlyoutTabs.ENGAGEMENT, FlyoutOpenState.OPEN, lessonElementId);
  });

  useAccessibilityClick(aiScoreButton, () => {
    setTimeout(() => {
      const announcementElement = document.getElementById(`${lessonElementId}-tab-announcement`);
      if (announcementElement) {
        announcementElement.textContent = `${t('aiScorePanel')} tab opened`;
      }
    }, 100);

    activateContentTab(FlyoutTabs.AI_SCORE, FlyoutOpenState.OPEN, lessonElementId);
  });

  useEffect(() => {
    if (variant === 'panel') {
      return undefined;
    }
    let goNextItem = false;
    let goPrevItem = false;
    let goNextStudent = false;
    let goPrevStudent = false;
    const onKeyDown = (e) => {
      if (e.ctrlKey && e.key === '\'') {
        goNextItem = true;
      } else if (e.ctrlKey && e.key === ';') {
        goPrevItem = true;
      } else if (e.ctrlKey && e.key === ']') {
        goNextStudent = true;
      } else if (e.ctrlKey && e.key === '[') {
        goPrevStudent = true;
      }
    };
    const onKeyUp = (e) => {
      if (e.key === '\'' && goNextItem) {
        navMenuService.nextSlideHandler(e);
      }
      if (e.key === ';' && goPrevItem) {
        navMenuService.previousSlideHandler(e);
      }
      if (e.key === ']' && goNextStudent) {
        if (studentActivityManager.instanceCount > 1) {
          studentActivityService.nextInstanceHandler(e);
        }
      }
      if (e.key === '[' && goPrevStudent) {
        if (studentActivityManager.instanceCount > 1) {
          studentActivityService.previousInstanceHandler(e);
        }
      }
      if (e.ctrlKey) {
        goNextItem = false;
        goPrevItem = false;
        goNextStudent = false;
        goPrevStudent = false;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      goNextItem = false;
      goPrevItem = false;
      goNextStudent = false;
      goPrevStudent = false;
    };
    // eslint-disable-next-line
  }, [])

  return ((openState) ? (
    <div className={classNames('flyout-wrapper',
      videoService.getVideoActivityParentClassNamesIfApplicable({ isScoreModeVideoActivityParent }), {
        wider: !embedded && (openState.open === FlyoutOpenState.OPEN_WIDER),
        wide: !embedded && (openState.open === FlyoutOpenState.OPEN_WIDE),
        open: !embedded && (openState.open === FlyoutOpenState.OPEN),
        left: (embedded && !embeddedInRightRail) || (!embedded && internalFlyoutSide === FlyoutSide.LEFT),
        closed: !embedded && (openState.open === FlyoutOpenState.CLOSED),
        activity: isActivity,
        'embedded-in-left-rail': embedded && !embeddedInRightRail,
        'embedded-in-right-rail': embeddedInRightRail,
        'tabs-only': variant === 'tabs',
        'panel-only': variant === 'panel',
        score: FlyoutTabs.SCORE === openState.tab,
        align: FlyoutTabs.ALIGN === openState.tab,
        notes: FlyoutTabs.NOTES === openState.tab || isCustomTeacherTabId(openState.tab),
        rubric: FlyoutTabs.RUBRIC === openState.tab,
        engagement: FlyoutTabs.ENGAGEMENT === openState.tab
      })}>
      {/* Live region for tab panel announcements */}
      <div
        aria-atomic='true'
        aria-live='assertive'
        className='sr-only'
        id={`${lessonElementId}-tab-announcement`}
      />

      {/* Live region for flyout status */}
      <div aria-atomic='true'
        aria-live='polite'
        className='sr-only'
        id={`${lessonElementId}-flyout-status`}>
        {openState.open === FlyoutOpenState.CLOSED ? 'Flyout menu closed' :
          openState.open === FlyoutOpenState.OPEN_WIDE ? 'Flyout menu opened wide' : 'Flyout menu opened'}
      </div>

      {variant !== 'panel' && (
      <div className={classNames({ 'flyout-tab-bar': true })}>
        {showFlipButton && (
          <div ref={flipButton}
            aria-label={(internalFlyoutSide === FlyoutSide.RIGHT) ? t2('moveFlyoutMenuLeft') : t2('moveFlyoutMenuRight')}
            className='flyout-tab flip-button-wrapper'
            role='button'
            tabIndex='0'>
            <div aria-hidden='true' className='icon-wrapper'>
              <div aria-hidden='true' className={classNames({ 'flip-button': true, 'left': (internalFlyoutSide === FlyoutSide.LEFT) })} />
            </div>
          </div>
        )}
        {showScoreTab && (
          <div ref={scoreButton}
            aria-describedby={`${lessonElementId}-flyout-status`}
            aria-label={(isTeacher) ? t3('headerLabelScore') : t3('headerLabelScore')}
            aria-pressed={isFlyoutTabSelected(FlyoutTabs.SCORE)}
            className={classNames({ 'flyout-tab': true, 'score-button': true, 'selected': isFlyoutTabSelected(FlyoutTabs.SCORE) })}
            role='button'
            tabIndex='0'>
            <div className={classNames({ 'flyout-tab-label': true, 'selected': isFlyoutTabSelected(FlyoutTabs.SCORE) })} tabIndex='-1'>
              {isSummary ? '' : t3('headerLabelScore')}
            </div>
          </div>
        )}
        {showAlignTab && (
          <div ref={alignButton} aria-label={t2('alignmentPanelTab')}
            aria-pressed={isFlyoutTabSelected(FlyoutTabs.ALIGN)} className={classNames({ 'flyout-tab': true, 'alignment-button': true, 'selected': isFlyoutTabSelected(FlyoutTabs.ALIGN) })}
            role='button'
            tabIndex='0'>
            <div className={classNames({ 'flyout-tab-label': true, 'selected': isFlyoutTabSelected(FlyoutTabs.ALIGN) })} tabIndex='-1'>{t('alignmentPanel')}</div>
          </div>
        )}
        {showNotesTab && (
          <div ref={teacherNotes} aria-label={t2('teacherFeedbackPanelTab')}
            aria-pressed={isFlyoutTabSelected(FlyoutTabs.NOTES)} className={classNames({ 'flyout-tab': true, 'notes-button': true, 'selected': isFlyoutTabSelected(FlyoutTabs.NOTES) })}
            role='button'
            tabIndex='0'>
            <div className={classNames({ 'flyout-tab-label': true, 'selected': isFlyoutTabSelected(FlyoutTabs.NOTES) })} tabIndex='-1'>{t('teacherNotePanel')}</div>
          </div>
        )}
        {showRubricTab && (
          <div ref={rubricButton}
            aria-expanded={openState.open === FlyoutOpenState.OPEN_WIDE && isFlyoutTabSelected(FlyoutTabs.RUBRIC)}
            aria-label={t2('rubricPanelTab')}
            aria-pressed={isFlyoutTabSelected(FlyoutTabs.RUBRIC)}
            className={classNames({ 'flyout-tab': true, 'rubric-button': true, 'selected': isFlyoutTabSelected(FlyoutTabs.RUBRIC) })}
            role='button'
            tabIndex='0'>
            <div className={classNames({ 'flyout-tab-label': true, 'selected': isFlyoutTabSelected(FlyoutTabs.RUBRIC) })} tabIndex='-1'>{t('rubricPanel')}</div>
          </div>
        )}
        {customTeacherTabs.map((tab) => {
          const customTabId = getCustomTeacherTabId(tab);
          const selected = isFlyoutTabSelected(customTabId);
          return (
            <div
              key={tab.id}
              aria-label={tab.fullTabName || tab.displayName}
              aria-pressed={selected}
              className={classNames({ 'flyout-tab': true, 'custom-teacher-tab-button': true, 'selected': selected })}
              role='button'
              tabIndex='0'
              title={tab.fullTabName || tab.displayName}
              onClick={() => {
                activateContentTab(customTabId, FlyoutOpenState.OPEN, lessonElementId);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  activateContentTab(customTabId, FlyoutOpenState.OPEN, lessonElementId);
                }
              }}>
              <div className={classNames({ 'flyout-tab-label': true, 'selected': selected })} tabIndex='-1'>{tab.displayName || tab.fullTabName}</div>
            </div>
          );
        })}
        {showEngagementTab && (
          <div ref={engageButton} aria-label={t2('engagementPanelTab')}
            aria-pressed={isFlyoutTabSelected(FlyoutTabs.ENGAGEMENT)} className={classNames({ 'flyout-tab': true, 'engagement-button': true, 'selected': isFlyoutTabSelected(FlyoutTabs.ENGAGEMENT) })}
            role='button'
            tabIndex='0'>
            <div className={classNames({ 'flyout-tab-label': true, 'selected': isFlyoutTabSelected(FlyoutTabs.ENGAGEMENT) })} tabIndex='-1'>{t('engagementPanel')}</div>
          </div>
        )}
        {showAIScoreTab && (
          <div ref={aiScoreButton}
            aria-describedby={`${lessonElementId}-flyout-status`}
            aria-label={t2('aiScorePanelTab')}
            aria-pressed={isFlyoutTabSelected(FlyoutTabs.AI_SCORE)}
            className={classNames({ 'flyout-tab': true, 'ai-score-button': true, 'selected': isFlyoutTabSelected(FlyoutTabs.AI_SCORE) })}
            role='button'
            tabIndex='0'>
            <div className={classNames({ 'flyout-tab-label': true, 'selected': isFlyoutTabSelected(FlyoutTabs.AI_SCORE) })} tabIndex='-1'>
              {t('aiScorePanel')}
            </div>
          </div>
        )}
      </div>
      )}
      {variant !== 'tabs' && (
      <div className={classNames({
        'flyout-panel-wrapper': true,
        'scoring': isScoring
      })} id={`${lessonElementId}-flyout-panel`}>
        {(isScoring) && (
          <FlyupNavForScoring
            flyoutSide={internalFlyoutSide}
            isActivity={isActivity}
            lessonElementId={lessonManager.currentLessonElementId} />
        )}
        <div className='flyout-panel'>
          {(FlyoutTabs.ALIGN === openState.tab) &&
            <AlignmentPanel lessonElementId={lessonElementId} />}
          {(FlyoutTabs.RUBRIC === openState.tab && mode !== LessonMode.SCORING) &&
            <RubricPanel lessonElementId={lessonElementId} />}
          {(FlyoutTabs.SCORE === openState.tab && isStudent) &&
            <StudentScorePanel lessonElementId={lessonElementId} />}
          {/* eslint-disable-next-line no-mixed-operators */}
          {((FlyoutTabs.SCORE === openState.tab && isTeacher) ||
              // eslint-disable-next-line no-mixed-operators
              FlyoutTabs.SCORE === openState.tab && isTeacher && model.isActivity) &&
              <TeacherScorePanel lessonElementId={lessonElementId} />}
          {(FlyoutTabs.NOTES === openState.tab && isTeacher) &&
            <TeacherNotesPanel lessonElementId={lessonElementId} />}
          {(isCustomTeacherTabId(openState.tab) && isTeacher) &&
            <CustomTeacherTabPanel lessonElementId={lessonElementId} tabId={openState.tab.replace('custom:', '')} />}
          {(FlyoutTabs.ENGAGEMENT === openState.tab && showEngagementTab) &&
            <EngagementPanel lessonElementId={lessonElementId} />}
          {(FlyoutTabs.AI_SCORE === openState.tab && showAIScoreTab) &&
            <TeacherAIScoreResultsPanel lessonElementId={lessonElementId} />}
        </div>
      </div>
      )}
    </div>
  ) :
    <></>);
});

export default Flyout;
